// K-07 Find walk-through (19 Oct 2026, items 47 to 50): which approvals and which spend belong to which module, and
// Settings › Approvals and spend › Outreach approvals's reads and writes (migration 20261226070000, act group approvals_find).
// - Every autonomy kind belongs to one module (KIND_MODULE); each module's Rules › Approvals page shows only its own.
// - Find's items: "Find relevant companies" (2.20, the automatic weekly pick, kept as the autonomy kind
//   find_companies, off by default) and drafting and sending outreach, by channel.
// - An item is "switched on" when its channel is connected (Accounts) and allowed (Sending rules); the rest sit under
//   "Not switched on yet".
// - Find's spend: one overall cap and a cap per paid service; other modules' spend is not shown here.
// L-06 (round 2, items 71, 72, 86): the items include social DMs and LinkedIn; per-campaign cells for the "What runs
// by itself" table; spend and caps in the business's currency (migration 20261227060000), letters included.
import type { Crm } from "./crm";
import type { AutonomyKind } from "./types";
import type { Campaign, CampaignApprovals } from "./campaigns";

export type ApprovalModule = "find" | "connect" | "attract" | "network" | "reconnect";

/** The autonomy kind for the automatic weekly pick of firms (2.20). */
export const FIND_PICK_KIND = "find_companies";

/** Which module each kind of work belongs to. A kind not listed is shown only in Settings (all modules). */
export const KIND_MODULE: Record<string, ApprovalModule> = {
  [FIND_PICK_KIND]: "find",
  first_touch_email: "find",
  follow_up_email: "find",
  right_contact_ask: "find",
  first_touch_letter: "find",
  follow_up_letter: "find",
  reply_answer: "connect",
  interested_reply: "connect",
  meeting_email: "connect",
  deal_email: "connect",
  connect_offer: "connect",
  whatsapp_reply: "connect",
  social_post: "attract",
  campaign_email: "attract",
  referral_ask: "network",
  referrer_email: "network",
  event_pitch: "network",
  event_meeting_offer: "network",
  event_follow_up: "network",
  nurture_note: "reconnect",
  history_follow_up: "reconnect",
  whatsapp_follow_up: "reconnect",
};

export function moduleOfKind(kind: string): ApprovalModule | null {
  return KIND_MODULE[kind] ?? null;
}

/** Keep only the kinds of one module; with no module, every kind. */
export function kindsForModule<T extends { kind: string }>(rows: T[], module?: ApprovalModule): T[] {
  return module ? rows.filter((r) => moduleOfKind(r.kind) === module) : rows;
}

export type FindChannel = "finding" | "email" | "letter" | "by_hand";

export interface FindItem {
  /** The key of a campaign's approvals (CampaignApprovals, shared with K-15's Rules › Campaigns). */
  key: FindApprovalKey;
  label: string;
  /** One line on what it is. */
  hint: string;
  channel: FindChannel;
  /** The autonomy kinds underneath (autonomy_levels): the item runs by itself only when every one of them does. */
  kinds: string[];
}

/** The per-campaign approval keys for Find's work (agreed with K-15; L-06 added social_dm and linkedin). */
export type FindApprovalKey = "find_firms" | "contact_lookup" | "first_email" | "follow_up" | "letter" | "social_dm" | "linkedin";

/** Find's own work, in the order the page shows it (L-06, item 71: the rows of "What runs by itself"). */
export const FIND_ITEMS: FindItem[] = [
  { key: "find_firms", label: "Find relevant companies", hint: "The automatic weekly pick of firms that fit your campaigns.", channel: "finding", kinds: [FIND_PICK_KIND] },
  { key: "contact_lookup", label: "Contact look-up", hint: "Finding who to speak to: a short email asking the firm, or a paid contact finder within your caps.", channel: "email", kinds: ["right_contact_ask"] },
  { key: "first_email", label: "First emails", hint: "The first email to someone new.", channel: "email", kinds: ["first_touch_email"] },
  { key: "follow_up", label: "Follow-up emails", hint: "Later emails in a sequence.", channel: "email", kinds: ["follow_up_email"] },
  { key: "letter", label: "Letters", hint: "Printed letters to owners with no email, first and follow-up.", channel: "letter", kinds: ["first_touch_letter", "follow_up_letter"] },
  { key: "social_dm", label: "Social DMs", hint: "Instagram and Facebook messages Ogma drafts and you send yourself.", channel: "by_hand", kinds: [] },
  { key: "linkedin", label: "LinkedIn messages", hint: "Connection requests and messages Ogma drafts and you send yourself.", channel: "by_hand", kinds: [] },
];

export const CHANNEL_NAME: Record<FindChannel, string> = { finding: "Finding firms", email: "Email", letter: "Letters", by_hand: "Sent by you" };

export interface ChannelState {
  connected: boolean;
  allowed: boolean;
}

export interface FindApprovalsState {
  pick: { on: boolean; changed_at: string | null; changed_by: string | null };
  kill: boolean;
  channels: { email: ChannelState; letter: ChannelState };
}

/** Why a channel is not switched on, or null when it is. */
export function notOnReason(channel: FindChannel, s: FindApprovalsState | null | undefined): string | null {
  if (channel === "finding" || channel === "by_hand" || !s) return null;
  const c = s.channels[channel];
  if (!c) return "Not set up yet";
  if (!c.connected && !c.allowed) return channel === "email" ? "No mailbox connected, and email is off in Sending rules" : "Letters not connected, and off in Sending rules";
  if (!c.connected) return channel === "email" ? "No mailbox connected yet" : "Letters service not connected yet";
  if (!c.allowed) return channel === "email" ? "Email is off in Sending rules" : "Letters are off in Sending rules";
  return null;
}

/** Items already switched on first, the rest after (item 48). */
export function splitSwitchedOn(items: FindItem[], s: FindApprovalsState | null | undefined): { on: FindItem[]; off: FindItem[] } {
  const on: FindItem[] = [];
  const off: FindItem[] = [];
  for (const it of items) (notOnReason(it.channel, s) ? off : on).push(it);
  return { on, off };
}

/** The Rules-level default for one item, as a campaign's approvals say it: "auto" runs by itself, "ask" waits. */
export function ruleDefault(item: FindItem, s: FindApprovalsState | null | undefined, stats: AutonomyKind[] | null | undefined): "ask" | "auto" {
  if (s?.kill) return "ask";
  if (item.key === "find_firms") return s?.pick.on ? "auto" : "ask";
  if (item.kinds.length === 0) return "ask";
  const levels = item.kinds.map((kind) => stats?.find((r) => r.kind === kind)?.level ?? "approve_all");
  return levels.every((l) => l !== "approve_all") ? "auto" : "ask";
}


// ---------------------------------------------------------------- per campaign (item 71)
/** What one cell of "What runs by itself" says: the campaign's own setting, or the Rules default it follows. */
export function campaignCell(item: FindItem, approvals: CampaignApprovals | null | undefined, dflt: "ask" | "auto", kill = false): { value: "ask" | "auto"; own: boolean } {
  if (kill) return { value: "ask", own: false };
  const own = approvals?.[item.key];
  return own === "ask" || own === "auto" ? { value: own, own: true } : { value: dflt, own: false };
}

/** The campaigns that get a column: running first (biggest share), then paused, then drafts; retired ones left out. */
export function tableCampaigns(list: Campaign[] | null | undefined): Campaign[] {
  const rank: Record<string, number> = { approved: 0, paused: 1, proposed: 2, draft: 3 };
  return (list ?? [])
    .filter((c) => c.status !== "retired")
    .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || b.share - a.share || a.name.localeCompare(b.name));
}

/** How many of a campaign's settings differ from the Rules, for its column. */
export function ownCount(approvals: CampaignApprovals | null | undefined): number {
  return Object.keys(approvals ?? {}).filter((k) => FIND_ITEMS.some((i) => i.key === k)).length;
}

// ---------------------------------------------------------------- spend (items 72 and 86)
export type FindService = "contact_finders" | "verification" | "screening" | "letters" | "calls";

export const SERVICE_INFO: Record<FindService, { label: string; providers: string }> = {
  contact_finders: { label: "Contact finders", providers: "BetterContact, FullEnrich, Hunter or Apollo" },
  verification: { label: "Email checks", providers: "MillionVerifier" },
  screening: { label: "Phone screening", providers: "DataSoap" },
  letters: { label: "Letters", providers: "Scribeless" },
  calls: { label: "Calls", providers: "Twilio or Vapi" },
};

/**
 * Find's default monthly caps in pounds (migration 20261227060000; other currencies are converted and rounded). Low on
 * purpose: a small business starting out spends little on look-ups, and a cap is easy to raise once results justify it.
 */
export const DEFAULT_CAPS_GBP: Record<FindService | "overall", number> = { overall: 50, contact_finders: 20, verification: 10, screening: 10, letters: 20, calls: 10 };

/** One line of Find's spend, in the business's currency (spent_usd and cap_usd are the same in dollars). */
export interface SpendLine {
  key: FindService | "overall";
  spent: number;
  cap: number;
  /** The owner set this cap (on this page, or for letters in the playbook). */
  set: boolean;
  /** The default cap, used when none is set. */
  default: number;
  over: boolean;
  /** Letters only: the cap comes from the playbook's letter settings, not this page. */
  from_settings?: boolean;
  spent_usd?: number;
  cap_usd?: number;
  vendors?: { vendor: string; spent: number }[];
}

export interface FindSpend {
  month_start: string;
  /** The business's currency (tenant settings; GBP for a UK business). */
  currency: string;
  /** Units of the currency per US dollar (vendors bill in dollars), and whether the rate is known. */
  rate: { per_usd: number; per_gbp: number; known: boolean };
  overall: SpendLine;
  services: SpendLine[];
  /** This month's letters: how many were sent and what each costs, in the business's currency. */
  letters: { sent_this_month: number; per_letter: number };
}

/** An amount in the business's currency, to the penny: "£12.40", "€5.00", "AED 90.00". */
export function money(n: number | null | undefined, currency = "GBP"): string {
  const code = /^[A-Z]{3}$/.test(currency) ? currency : "GBP";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n ?? 0));
  } catch {
    return `${code} ${Number(n ?? 0).toFixed(2)}`;
  }
}

/** One line for the spend card: "£12.40 of £50.00 this month" or "Cap reached: Contact finders". */
export function spendLine(s: FindSpend | null | undefined): string {
  if (!s) return "Loading Find's spend";
  const m = (n: number) => money(n, s.currency);
  const over = s.services.filter((x) => x.over).map((x) => SERVICE_INFO[x.key as FindService]?.label ?? x.key);
  if (s.overall.over) return `Find's cap reached: ${m(s.overall.spent)} of ${m(s.overall.cap)} this month`;
  if (over.length) return `Cap reached: ${over.join(", ")}`;
  return `${m(s.overall.spent)} of ${m(s.overall.cap)} this month`;
}

/** The letters line's detail: "3 letters sent this month at £1.50 each". */
export function lettersDetail(s: FindSpend): string {
  const n = s.letters?.sent_this_month ?? 0;
  return `${n} ${n === 1 ? "letter" : "letters"} sent this month at ${money(s.letters?.per_letter ?? 0, s.currency)} each`;
}

/** A plain note on the dollar rate, for a business not paying in dollars. */
export function rateNote(s: FindSpend): string | null {
  if (s.currency === "USD") return null;
  const per = Number(s.rate?.per_usd ?? 1);
  if (!s.rate?.known) return `Services that bill in dollars are counted at $1 = 1 ${s.currency} until you set a rate in your playbook.`;
  return `Services that bill in dollars are counted at about $1 = ${money(per, s.currency)}.`;
}

// ---------------------------------------------------------------- calls

export function findApprovals(crm: Crm): Promise<FindApprovalsState> {
  return crm.read<FindApprovalsState>("crm_find_approvals");
}

export function findSpend(crm: Crm): Promise<FindSpend> {
  return crm.read<FindSpend>("crm_find_spend");
}

export function setFindPick(crm: Crm, on: boolean): Promise<{ on: boolean }> {
  return crm.write("set_find_pick", { on });
}

/** One of Find's caps in the business's currency; null goes back to the default. */
export function setFindSpendCap(crm: Crm, service: FindService | "overall", amount: number | null): Promise<FindSpend> {
  return crm.write("set_find_spend_cap", { service, amount });
}
